import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../../../../../components/Navbar';
import Footer from '../../../../../home/components/Footer';

const AREA = 'Rickmansworth';
const AREA_SLUG = 'rickmansworth';
const POSTCODE = 'WD3';
const PHONE = '020 3198 1818';
const PHONE_RAW = '02031981818';

const services = [
  { icon: 'ri-tools-line', title: 'MCB Replacement', desc: 'Replacing faulty miniature circuit breakers that trip or won\'t reset in your Rickmansworth home.' },
  { icon: 'ri-flashlight-line', title: 'RCD Repairs', desc: 'Fixing or replacing residual current devices that cause nuisance tripping or fail safety tests.' },
  { icon: 'ri-cpu-line', title: 'Busbar Repairs', desc: 'Repairing damaged busbars and internal connections inside the consumer unit.' },
  { icon: 'ri-switch-line', title: 'Main Switch Replacement', desc: 'Replacing worn or faulty main isolator switches on older Rickmansworth properties.' },
  { icon: 'ri-file-list-line', title: 'Circuit Labelling', desc: 'Properly labelling all circuits for safety and future maintenance.' },
  { icon: 'ri-shield-check-line', title: 'Safety Inspections', desc: 'Full consumer unit inspection and testing to confirm safe, compliant operation.' },
];

const signs = [
  'Breakers trip and refuse to reset', 'Burning smell from the consumer unit',
  'Scorch marks or discolouration on the unit', 'Unit feels warm or hot to touch',
  'Buzzing or humming from the fuse box', 'Visible loose wires or damaged casing',
  'Lights flickering across multiple circuits', 'Frequently blown fuses in older properties',
];

export default function RickmansworthFuseBoxRepairs() {
  useEffect(() => {
    document.title = `Fuse Box Repairs ${AREA} ${POSTCODE} | Beyond Electrical`;
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', `Expert fuse box & consumer unit repairs in ${AREA}, ${POSTCODE}. Fix tripping RCDs, faulty MCBs & electrical panel faults. NICEIC certified. Call ${PHONE}.`);
    const metaKw = document.querySelector('meta[name="keywords"]');
    if (metaKw) metaKw.setAttribute('content', `fuse box repairs ${AREA}, consumer unit repair ${AREA}, electrician ${AREA}, MCB replacement ${POSTCODE}, RCD repair ${AREA}`);
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute('href', `https://beyondelectrical.co.uk/services/repairs/fuse-box-repairs/${AREA_SLUG}`);
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({ '@context': 'https://schema.org', '@type': 'Service', 'serviceType': 'Fuse Box Repairs', 'provider': { '@type': 'LocalBusiness', 'name': 'Beyond Electrical', 'telephone': PHONE_RAW }, 'areaServed': { '@type': 'City', 'name': AREA } });
    document.head.appendChild(script);
    return () => { document.head.removeChild(script); };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <section className="relative h-[500px] flex items-center justify-center text-white overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://public.readdy.ai/ai/img_res/d71473116225cba2034a310f18e56013.jpg" alt={`Fuse Box Repairs in ${AREA}`} className="w-full h-full object-cover object-top" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A1628]/90 via-[#0A1628]/80 to-[#0F2847]/70"></div>
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center w-full">
          <span className="inline-block px-4 py-2 bg-[#FFD700] text-[#0A1628] rounded-full text-sm font-semibold mb-4 whitespace-nowrap">{AREA} Fuse Box Specialists</span>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Fuse Box Repairs in {AREA}</h1>
          <p className="text-xl mb-8 text-gray-200">NICEIC certified consumer unit repairs for {AREA} homes &amp; businesses. Fast, safe, guaranteed.</p>
          <div className="flex flex-wrap gap-4 justify-center">
            <a href={`tel:${PHONE_RAW}`} className="px-8 py-4 bg-[#FFD700] text-[#0A1628] rounded-lg font-semibold hover:bg-[#FFD700]/90 transition-all whitespace-nowrap flex items-center gap-2"><i className="ri-phone-line"></i> Call {PHONE}</a>
            <Link to="/contact" className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white border-2 border-[#FFD700] rounded-lg font-semibold hover:bg-white/20 transition-all whitespace-nowrap">Get Free Quote</Link>
          </div>
        </div>
      </section>
      <section className="py-20 px-6 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-[#0A1628] mb-4">Fuse Box Repair Services in {AREA}</h2>
          <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">Comprehensive consumer unit repairs for all faults across {AREA} and {POSTCODE}</p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((s, i) => (
              <div key={i} className="bg-white p-6 rounded-xl border border-gray-100 hover:border-amber-400 transition-all">
                <div className="w-12 h-12 flex items-center justify-center bg-amber-500 rounded-lg mb-4"><i className={`${s.icon} text-2xl text-black`}></i></div>
                <h3 className="text-lg font-bold text-[#0A1628] mb-2">{s.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section className="py-20 px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-[#0A1628] mb-4">Warning Signs Your Fuse Box Needs Repair</h2>
          <p className="text-center text-gray-600 mb-12">Don&apos;t ignore these danger signals — contact us if you notice any of these in your {AREA} property</p>
          <div className="grid md:grid-cols-2 gap-4 max-w-4xl mx-auto">
            {signs.map((sign, i) => (
              <div key={i} className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                <i className="ri-alert-line text-xl text-amber-500 flex-shrink-0 mt-0.5"></i>
                <p className="text-gray-700 text-sm">{sign}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section className="py-20 bg-gradient-to-br from-[#0A1628] via-[#0F1F3A] to-[#0A1628] text-white">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">Why {AREA} Residents Choose Beyond Electrical</h2>
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {[{ icon: 'ri-time-line', title: 'Same-Day Response', desc: `Fast response for urgent fuse box faults across ${AREA}` }, { icon: 'ri-shield-check-line', title: 'NICEIC Certified', desc: 'All repairs certified and compliant with BS7671 wiring regulations' }, { icon: 'ri-award-line', title: '15+ Years Experience', desc: `Trusted by hundreds of homeowners across ${POSTCODE} and beyond` }].map((item, i) => (
              <div key={i} className="text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-[#FFD700] rounded-full mx-auto mb-4"><i className={`${item.icon} text-3xl text-[#0A1628]`}></i></div>
                <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                <p className="text-gray-300 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {[['400+', 'Fuse Boxes Repaired'], ['100%', 'Satisfaction Rate'], ['24/7', 'Emergency Service'], ['15+', 'Years Experience']].map(([num, label], i) => (<div key={i}><div className="text-4xl font-bold text-[#D4AF37] mb-1">{num}</div><div className="text-gray-300 text-sm">{label}</div></div>))}
          </div>
        </div>
      </section>
      <section className="py-20 px-6 bg-gray-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-[#0A1628] mb-4">Ready to Book a Fuse Box Repair in {AREA}?</h2>
          <p className="text-gray-600 mb-8">Serving {AREA}, {POSTCODE} and all surrounding areas.</p>
          <div className="flex flex-wrap gap-4 justify-center mb-8">
            <a href={`tel:${PHONE_RAW}`} className="px-8 py-4 bg-[#FFD700] text-[#0A1628] rounded-lg font-semibold hover:bg-[#FFD700]/90 transition-all whitespace-nowrap flex items-center gap-2"><i className="ri-phone-line"></i> Call {PHONE}</a>
            <Link to="/contact" className="px-8 py-4 bg-[#0A1628] text-white rounded-lg font-semibold hover:bg-[#0F2847] transition-all whitespace-nowrap">Request a Quote</Link>
          </div>
          <Link to="/services/repairs/fuse-box-repairs" className="inline-flex items-center gap-2 text-[#D4AF37] hover:text-[#B8941F] font-medium transition-colors"><i className="ri-arrow-left-line"></i> View All Fuse Box Repair Services</Link>
        </div>
      </section>
      <Footer />
    </div>
  );
}
